import { useAtomValue } from "@effect/atom-react";
import { useMemo } from "react";

import type { EnvironmentId, ModelCatalog, ServerSettings } from "@cleo/contracts";

import { toastManager } from "../components/ui/toast";
import {
  applyProviderInstanceSettings,
  deriveProviderInstanceEntries,
  sortProviderInstanceEntries,
  type ProviderInstanceEntry,
} from "../providerInstances";
import { useEnvironments, usePrimaryEnvironmentId } from "../state/environments";
import { primaryServerProvidersAtom } from "../state/server";
import { readModelCatalog } from "./modelCatalogClient";

/**
 * How long a root-session start waits for the World's model catalog before
 * it keeps today's default. The catalog is one local read the picker
 * already makes; a slow World must not hold the + click hostage.
 */
export const ROOT_SESSION_CATALOG_TIMEOUT_MS = 2_000;

/**
 * The launch environment's provider instances in the model picker's order:
 * the composer's own derivation (settings overlay, then default instance
 * first per driver). Empty until that environment reports its providers.
 */
export function useRootSessionProviderEntries(
  environmentId: EnvironmentId | null,
  settings: Pick<ServerSettings, "providerInstances" | "providers">,
): ReadonlyArray<ProviderInstanceEntry> {
  const { environments } = useEnvironments();
  const primaryEnvironmentId = usePrimaryEnvironmentId();
  const primaryProviders = useAtomValue(primaryServerProvidersAtom);
  const providers =
    environmentId === null
      ? []
      : (environments.find((environment) => environment.environmentId === environmentId)
          ?.serverConfig?.providers ??
        (environmentId === primaryEnvironmentId ? primaryProviders : []));
  return useMemo(
    () =>
      providers.length === 0
        ? []
        : sortProviderInstanceEntries(
            applyProviderInstanceSettings(deriveProviderInstanceEntries(providers), settings),
          ),
    [providers, settings],
  );
}

/** The World's model catalog, or null when it cannot be read within the timeout. */
export async function readRootSessionModelCatalog(
  worldKey: string,
  timeoutMs: number = ROOT_SESSION_CATALOG_TIMEOUT_MS,
): Promise<ModelCatalog | null> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      readModelCatalog(worldKey),
      new Promise<null>((resolve) => {
        timer = setTimeout(() => resolve(null), timeoutMs);
      }),
    ]);
  } catch {
    return null;
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

/** Say why the session chose another model where notices surface. */
export function announceRootSessionModelNote(note: string): void {
  toastManager.add({ type: "warning", title: "Session model changed", description: note });
}
